<template>
  <div ref="timePicker">
    <el-time-picker
      v-model="time"
      :placeholder="placeholder"
      :format="format"
      :clearable="clearable"
      :editable="editable"
      size="large"
      @blur="blurEvent"
    />
    <slot name="comment" />
  </div>
</template>

<script setup lang="ts">
import { ref, Ref, computed, WritableComputedRef, watch, nextTick, onMounted } from 'vue';

const props = defineProps({
  modelValue: {
    type: [Date, String],
    default: null,
  },

  required: {
    type: Boolean,
    default: false,
  },

  editable: {
    type: Boolean,
    default: false,
  },

  clearable: {
    type: Boolean,
    default: false,
  },

  placeholder: {
    type: String,
    default: 'Select Time',
  },

  format: {
    type: String,
    default: 'hh:mm A',
  },
});

const emit = defineEmits(['update:modelValue', 'blur']);

const timePicker: Ref<any> = ref(null);

const time: WritableComputedRef<any> = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emit('update:modelValue', value);
  },
});

const addRequiredAttribute = (inputElement: HTMLInputElement) => {
  inputElement.readOnly = false;
  inputElement.required = true;
};

const removeRequiredAttribute = (inputElement: HTMLInputElement) => {
  inputElement.readOnly = true;
  inputElement.required = false;
};

const blurEvent = (e: FocusEvent) => {
  emit('blur', e);
};

watch(
  () => props.required,
  (value: boolean) => {
    const inputElement = timePicker.value?.querySelector('input.el-input__inner') as HTMLInputElement;

    if (!inputElement) {
      return;
    }

    if (value) {
      addRequiredAttribute(inputElement);
    } else {
      removeRequiredAttribute(inputElement);
    }
  },
);

onMounted(async () => {
  await nextTick();

  const inputElement = timePicker.value?.querySelector('input.el-input__inner');
  if (props.required && inputElement) {
    addRequiredAttribute(inputElement);
  }
});
</script>

<style lang="scss">
.el-date-editor.el-date-editor--time {
  input.el-input__inner {
    text-transform: uppercase;
  }
}
</style>
